<template>
  <div class="min-h-screen flex flex-col">
    <!-- 背景装饰 -->
    <BackgroundPatterns />

    <!-- 顶部栏 -->
    <header
      class="fixed inset-x-0 top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-200/50"
    >
      <div class="max-w-8xl mx-auto px-4 sm:px-6 py-3">
        <div class="flex items-center justify-between">
          <router-link
            to="/"
            class="flex items-center h-8 hover:scale-105 transition-transform"
          >
            <img
              :src="systemLogo"
              alt="Aberdeen Institute"
              class="h-full w-auto object-contain"
            />
          </router-link>
          <div class="flex items-center gap-2">
            <LanguageSelector />
            <StatusIndicator class="hidden sm:inline-flex" />
          </div>
        </div>
      </div>
    </header>

    <!-- 主内容 -->
    <main
      class="flex-1 flex flex-col justify-center items-center px-4 sm:px-6 py-8 min-h-[calc(100vh-4rem)]"
    >
      <div class="w-full max-w-md sm:max-w-lg">
        <!-- 标题 -->
        <div class="text-center mb-8">
          <h2
            class="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-3"
          >
            {{
              t("common.welcomeLogin", {
                systemShortName: brandConfig.systemShortName,
              })
            }}
          </h2>
          <p class="text-sm sm:text-base text-gray-500">
            {{
              t("common.loginSubtitle", {
                systemFullName: brandConfig.systemFullName,
              })
            }}
          </p>
        </div>

        <!-- 登录表单 -->
        <div
          class="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-8 sm:p-10"
        >
          <form @submit.prevent="handleSubmit" class="space-y-5">
            <!-- 用户名 -->
            <div>
              <label
                for="username"
                class="block text-sm font-medium text-gray-900 mb-2"
              >
                {{ t("common.username") }}
              </label>
              <input
                id="username"
                v-model="formData.username"
                name="username"
                type="text"
                autocomplete="username"
                required
                :disabled="isLoading"
                class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                :placeholder="t('form.enterUsername')"
              />
              <p class="mt-1 text-xs text-gray-500">
                {{
                  isZhLocale
                    ? "用户名为您的中方学工号"
                    : "Username is your university email address"
                }}
              </p>
            </div>

            <!-- Password Field -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label
                  for="password"
                  class="block text-sm font-medium text-gray-900"
                >
                  {{ t("common.password") }}
                </label>
                <router-link
                  :to="{ name: 'password-forgot' }"
                  class="text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  {{ t("form.forgotPassword") }}
                </router-link>
              </div>
              <div class="relative">
                <input
                  id="password"
                  v-model="formData.password"
                  :type="showPassword ? 'text' : 'password'"
                  name="password"
                  autocomplete="current-password"
                  required
                  :disabled="isLoading"
                  class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  :placeholder="t('form.enterPassword')"
                  @keydown="handleKeyDown"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  :aria-label="
                    showPassword
                      ? t('form.hidePassword')
                      : t('form.showPassword')
                  "
                >
                  <EyeIcon v-if="showPassword" class="h-5 w-5" />
                  <EyeSlashIcon v-else class="h-5 w-5" />
                </button>
              </div>

              <!-- 大写锁定提示 -->
              <p v-if="capsLockWarning" class="text-amber-600 text-xs mt-1">
                {{ t("form.capsLockOn") }}
              </p>
            </div>

            <!-- 验证码（连续失败后显示） -->
            <div v-if="showCaptcha">
              <label
                for="captcha"
                class="block text-sm font-medium text-gray-900 mb-2"
              >
                {{ t("common.verifyCode") }}
              </label>
              <div class="flex gap-3">
                <input
                  id="captcha"
                  v-model="formData.captcha"
                  name="captcha"
                  type="text"
                  required
                  :disabled="isLoading"
                  class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  :placeholder="t('form.enterVerifyCode')"
                />
                <button
                  type="button"
                  @click="refreshCaptcha"
                  :disabled="isLoading"
                  class="flex items-center justify-center w-24 bg-blue-600 text-white font-mono text-lg tracking-wider rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                  {{ captchaCode }}
                </button>
              </div>
            </div>

            <!-- 提交按钮 -->
            <div class="pt-2">
              <button
                type="submit"
                :disabled="isLoading || !isFormValid"
                class="group relative w-full flex justify-center items-center rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 text-base font-semibold text-white shadow-lg hover:from-blue-700 hover:to-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:from-blue-400 disabled:to-blue-400 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
              >
                <div
                  class="absolute inset-0 rounded-xl bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                ></div>
                <LoadingSpinner
                  v-if="isLoading"
                  class="mr-2 h-5 w-5 relative z-10"
                />
                <ArrowRightOnRectangleIcon
                  v-else
                  class="mr-2 h-5 w-5 relative z-10"
                />
                <span class="relative z-10">{{ t("common.login") }}</span>
              </button>

              <!-- 口令钥登录选项 -->
              <div v-if="isPasskeyAvailable" class="mt-6">
                <div class="relative">
                  <div class="absolute inset-0 flex items-center">
                    <div class="w-full border-t border-gray-200"></div>
                  </div>
                  <div class="relative flex justify-center text-xs uppercase">
                    <span class="bg-white px-3 text-gray-500">
                      {{ isZhLocale ? "或" : "or" }}
                    </span>
                  </div>
                </div>
                <div class="mt-4 flex justify-center">
                  <router-link
                    :to="{ path: '/passkey/login', query: $route.query }"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <FingerPrintIcon class="h-4 w-4 mr-2" />
                    <span>{{ t("passkey.login") }}</span>
                  </router-link>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useBrandConfig } from "@/utils/brand";
import { notificationService } from "@/services/notificationService";
import { jwtLifecycleService } from "@/services/jwtLifecycleService";
import { errorRecoveryService } from "@/services/errorRecoveryService";
import { fingerprintService } from "@/services/fingerprint";
import { useAuthStore } from "@/stores/auth";
import BackgroundPatterns from "@/components/layout/BackgroundPatterns.vue";
import LanguageSelector from "@/components/common/LanguageSelector.vue";
import StatusIndicator from "@/components/common/StatusIndicator.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { getSystemConfig } from "@/router/systems";
import { getJumpUrl } from "@/api/auth";
import { decodeJumpUrl } from "@/utils/url";
import { reportError } from "@/utils/sentry";
import { systemConfigService } from "@/services/systemConfigService";
import {
  ArrowRightOnRectangleIcon,
  EyeIcon,
  EyeSlashIcon,
  FingerPrintIcon,
} from "@heroicons/vue/24/outline";
import { passkeySupport } from "@/services/passkeySupportService";

// Composables
const { t, locale } = useI18n();
const brandConfig = useBrandConfig();
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

// 获取系统logo路径
const systemLogo = computed(() => {
  return systemConfigService.systemLogo;
});

// Computed properties
const isZhLocale = computed(
  () => locale.value === "zh-CN" || locale.value === "zh",
);
const isPasskeyAvailable = computed(
  () => passkeySupport.isEnabled() && passkeySupport.supportedRef.value === true,
);

const isFormValid = computed(() => {
  return (
    formData.value.username.trim() &&
    formData.value.password.trim() &&
    (!showCaptcha.value || formData.value.captcha.trim())
  );
});

// Reactive state
const formData = ref({
  username: "",
  password: "",
  captcha: "",
  fingerprint: "",
});

const isLoading = ref(false);
const showPassword = ref(false);
const capsLockWarning = ref(false);
const failedAttempts = ref(0);
const showCaptcha = computed(() => failedAttempts.value >= 3);
const captchaCode = ref(generateCaptcha());

// Simple captcha generation
function generateCaptcha(): string {
  return Math.random().toString(36).substring(2, 6).toUpperCase();
}

function refreshCaptcha(): void {
  if (!isLoading.value) {
    captchaCode.value = generateCaptcha();
    formData.value.captcha = "";
  }
}

// Caps Lock detection (simplified)
function handleKeyDown(event: KeyboardEvent): void {
  capsLockWarning.value = event.getModifierState("CapsLock");
}

// Clear caps lock warning when password field loses focus
function clearCapsLockWarning(): void {
  capsLockWarning.value = false;
}

// Main form submission handler
async function handleSubmit(): Promise<void> {
  if (isLoading.value || !isFormValid.value) return;

  try {
    isLoading.value = true;

    // Validate captcha if required
    if (
      showCaptcha.value &&
      formData.value.captcha.toUpperCase() !== captchaCode.value
    ) {
      notificationService.error("form.invalidVerifyCode");
      refreshCaptcha();
      return;
    }

    // Get device fingerprint
    try {
      formData.value.fingerprint = await fingerprintService.getFingerprint();
    } catch (error) {
      console.warn("Failed to get fingerprint:", error);
      // Continue login without fingerprint
    }

    // Perform login
    const response = await authStore.handleLogin(
      formData.value.username,
      formData.value.password,
      formData.value.fingerprint,
    );

    // Handle special status codes
    if (response?.status === 520) {
      notificationService.info("auth.setEmailFirst");
      await router.push({
        path: "/email",
        query: route.query,
      });
      return;
    }

    // Login successful
    notificationService.success("common.loginSuccess");

    // Start JWT lifecycle management
    jwtLifecycleService.start();

    // Handle redirects
    await handlePostLoginRedirect();
  } catch (error: any) {
    failedAttempts.value++;

    if (error.response?.status === 401 || error.response?.status === 403) {
      notificationService.error("common.loginFailed");
    } else {
      notificationService.error("common.systemError");
      reportError(error instanceof Error ? error : new Error(String(error)), {
        component: "LoginView",
        action: "login",
        username: formData.value.username,
        failedAttempts: failedAttempts.value,
      });
    }

    if (showCaptcha.value) {
      refreshCaptcha();
    }
  } finally {
    isLoading.value = false;
  }
}

// Handle post-login redirects
async function handlePostLoginRedirect(): Promise<void> {
  console.log("🔄 LoginView: Processing post-login redirects", {
    query: route.query,
    hasJumpTo: !!route.query.jump_to,
    hasNext: !!route.query.next,
    hasJumpAlias: !!route.query.jump_alias,
    hasRedirect: !!route.query.redirect,
  });

  // Check for jump_to parameter (external redirect)
  const jumpTo = route.query.jump_to as string;
  if (jumpTo) {
    console.log("🔄 LoginView: Processing jump_to parameter", jumpTo);
    const decodedUrl = decodeJumpUrl(jumpTo);
    if (decodedUrl) {
      console.log("🔄 LoginView: Redirecting to jump_to URL", decodedUrl);
      window.location.href = decodedUrl;
      return;
    }
  }

  // Check for next parameter (similar to jump_to)
  const nextParam = route.query.next as string;
  if (nextParam) {
    console.log("🔄 LoginView: Processing next parameter", nextParam);
    const decodedUrl = decodeJumpUrl(nextParam);
    if (decodedUrl) {
      console.log("🔄 LoginView: Redirecting to next URL", decodedUrl);
      window.location.href = decodedUrl;
      return;
    }
  }

  // Check for jump_alias parameter
  const jumpAlias = route.query.jump_alias as string;
  if (jumpAlias) {
    console.log("🔄 LoginView: Processing jump_alias parameter", jumpAlias);
    try {
      const response = await getJumpUrl(jumpAlias);
      if (response?.url) {
        console.log(
          "🔄 LoginView: Redirecting to jump_alias URL",
          response.url,
        );
        window.location.href = response.url;
        return;
      }
    } catch (error) {
      console.error("🔄 LoginView: Failed to get jump URL:", error);
      reportError(error instanceof Error ? error : new Error(String(error)), {
        component: "LoginView",
        action: "getJumpUrl",
        alias: jumpAlias,
      });
    }
  }

  // Handle redirect parameter
  const redirect = route.query.redirect as string;
  if (redirect) {
    console.log("🔄 LoginView: Processing redirect parameter", redirect);
    await router.replace({
      path: redirect,
      query: {
        wechat_openid: route.query.wechat_openid,
      },
    });
    return;
  }

  // Default system redirect
  console.log("🔄 LoginView: Using default system redirect");
  await handleSystemRedirect();
}

// Simplified system redirect
async function handleSystemRedirect(): Promise<void> {
  const systemConfig = route.meta.system || getSystemConfig(route.path);
  const targetPath = systemConfig ? systemConfig.path : "/authorize";

  try {
    await router.replace({
      path: targetPath,
      query: route.query,
    });
  } catch (error) {
    console.error("System redirect failed:", error);

    // Fallback to error recovery
    await errorRecoveryService.forceRecovery({
      reason: "redirect_failure",
      redirectPath: targetPath,
    });
  }
}

// Event listeners setup
onMounted(() => {
  // Add blur listener to password field for caps lock warning
  const passwordField = document.getElementById("password");
  if (passwordField) {
    passwordField.addEventListener("blur", clearCapsLockWarning);
  }

  // 预检 Passkey 支持（缓存结果，避免后续第一次显示延迟）
  passkeySupport.detectSupport().catch(() => {});
});

onUnmounted(() => {
  // Clean up event listeners
  const passwordField = document.getElementById("password");
  if (passwordField) {
    passwordField.removeEventListener("blur", clearCapsLockWarning);
  }
});
</script>

<style scoped>
/* Remove custom background since BackgroundPatterns handles it */
.min-h-screen {
  position: relative;
}

/* Ensure main content is above background */
main {
  position: relative;
  z-index: 10;
}

/* Enhanced focus states */
input:focus-visible {
  outline: 2px solid rgb(59, 130, 246);
  outline-offset: 2px;
}

button:focus-visible {
  outline: 2px solid rgb(59, 130, 246);
  outline-offset: 2px;
}

/* Button hover effects - enhanced for new gradient design */
button[type="submit"]:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px -5px rgba(59, 130, 246, 0.4);
}

button[type="submit"]:active:not(:disabled) {
  transform: translateY(0);
}

/* Smooth transitions */
input,
button {
  transition: all 0.2s ease;
}

/* Header backdrop blur enhancement */
header {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  z-index: 50;
}

/* Form card enhanced glass morphism effect */
.bg-white\/80 {
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow:
    0 8px 32px -8px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

/* Input field enhancements */
input {
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

input:hover {
  border-color: rgb(147, 197, 253);
  background-color: rgba(255, 255, 255, 0.9);
}

input:focus {
  background-color: rgba(255, 255, 255, 0.95);
}

/* WebKit autofill styling */
input:-webkit-autofill {
  transition: background-color 50000s ease-in-out 0s;
  -webkit-box-shadow: 0 0 0 30px rgba(255, 255, 255, 0.9) inset !important;
  -webkit-text-fill-color: rgb(17, 24, 39) !important;
}

/* Loading spinner */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Improved button states */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

/* Enhanced captcha button */
button[type="button"] {
  transition: all 0.2s ease;
}

button[type="button"]:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.3);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .max-w-md {
    max-width: 100%;
    padding: 0 1rem;
  }

  .bg-white\/80 {
    margin: 0 0.5rem;
    padding: 2rem 1.5rem !important;
  }

  .text-2xl {
    font-size: 1.5rem;
  }

  .text-3xl {
    font-size: 1.875rem;
  }

  /* Mobile: Adjust main container */
  main {
    min-height: calc(100vh - 3rem);
    padding: 1rem 1rem 2rem 1rem;
  }

  /* Mobile: Adjust spacing */
  .mb-8 {
    margin-bottom: 1.5rem;
  }

  /* Mobile: Adjust input fields */
  input {
    font-size: 16px; /* Prevent zoom on iOS */
  }
}

@media (min-width: 1024px) {
  .max-w-md {
    max-width: 28rem;
  }

  .lg\\:max-w-xl {
    max-width: 36rem;
  }
}

/* Enhanced glass morphism for better visual hierarchy */
.bg-white\/80:hover {
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow:
    0 12px 40px -8px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

/* Subtle animation for the form card */
.bg-white\/80 {
  animation: slideIn 0.6s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
